// Makes dropdowns look attached to the control that opened them.
//
// Blueprint doesn't say which edge of its trigger a popover lines up with, so
// when a popover opens we compare the two boxes and tag the shared edge:
//
//   .popover-attached--start / --end   on the popover (square top corner)
//   .popover-trigger--start / --end    on the trigger (square bottom corner)
//
// Styles live in styles/search-filters-bar.scss. Tooltips are left alone.

const EDGE_TOLERANCE = 2; // px
const MAX_GAP = 8; // px between trigger bottom and popover top

const POPOVER_CLASSES = ['popover-attached--start', 'popover-attached--end'];
const TRIGGER_CLASSES = ['popover-trigger--start', 'popover-trigger--end'];

// The visible control inside a popover target (button, input, ...).
const controlOf = (target) => target.firstElementChild || target;

const clear = (element, classes) => {
  if (element) element.classList.remove(...classes);
};

const tag = (popover) => {
  if (!popover.isConnected || popover.classList.contains('bp3-tooltip')) return;
  const targets = document.querySelectorAll('.bp3-popover-target.bp3-popover-open');
  const box = popover.getBoundingClientRect();
  if (!box.width) return;

  targets.forEach((target) => {
    const control = controlOf(target);
    const anchor = control.getBoundingClientRect();
    const below = box.top >= anchor.bottom - 1 && box.top - anchor.bottom <= MAX_GAP;
    if (!below) return;

    const start = Math.abs(box.left - anchor.left) <= EDGE_TOLERANCE;
    const end = Math.abs(box.right - anchor.right) <= EDGE_TOLERANCE;
    if (!start && !end) return;

    clear(popover, POPOVER_CLASSES);
    if (start) popover.classList.add('popover-attached--start');
    if (end) popover.classList.add('popover-attached--end');

    // Untag the trigger once its popover goes away.
    clear(control, TRIGGER_CLASSES);
    if (start) control.classList.add('popover-trigger--start');
    if (end) control.classList.add('popover-trigger--end');
    const untag = new MutationObserver(() => {
      if (!popover.isConnected || !target.classList.contains('bp3-popover-open')) {
        clear(control, TRIGGER_CLASSES);
        untag.disconnect();
      }
    });
    untag.observe(target, { attributes: true, attributeFilter: ['class'] });
  });
};

// Popper positions the popover after it mounts and may reposition it, so
// measure on the next frames rather than immediately.
const tagSoon = (popover) => {
  requestAnimationFrame(() => {
    tag(popover);
    requestAnimationFrame(() => tag(popover));
  });
  setTimeout(() => tag(popover), 120);
};

export const watchAttachedPopovers = () => {
  if (typeof MutationObserver === 'undefined' || window.__attachedPopovers) return;
  window.__attachedPopovers = true;
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const popovers = node.matches && node.matches('.bp3-popover')
          ? [node]
          : node.querySelectorAll ? node.querySelectorAll('.bp3-popover') : [];
        popovers.forEach(tagSoon);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
};
